import { Check, ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const PLANS = [
  { id: "basis", price: "15.000 KSH", features: 4, popular: false },
  { id: "plus", price: "30.000 KSH", features: 5, popular: true },
  { id: "pro", price: "50.000 KSH", features: 5, popular: false },
];

export const Pricing = ({ onNavigate }) => {
  const { t } = useLanguage();

  return (
    <section id="priser" data-testid="pricing-section" className="border-t border-[#262626] bg-[#0A0A0A]">
      <div className="mx-auto max-w-7xl px-6 py-32 md:px-12 md:py-48">
        <span className="text-sm uppercase tracking-[0.3em] text-[#FF3333]">{t("pricing.label")}</span>
        <h2 className="mt-4 font-syne font-extrabold uppercase tracking-tighter text-[#F2F2F2]">
          <MaskedLine innerClassName="text-4xl sm:text-5xl lg:text-6xl">{t("pricing.h1")}</MaskedLine>
          <MaskedLine delay={0.12} innerClassName="text-4xl sm:text-5xl lg:text-6xl text-stroke">
            {t("pricing.h2")}
          </MaskedLine>
        </h2>
        <FadeUp delay={0.2} className="mt-6 max-w-lg">
          <p className="text-base text-[#A3A3A3] md:text-lg">{t("pricing.intro")}</p>
        </FadeUp>

        <div className="mt-20 grid grid-cols-1 gap-8 md:grid-cols-3">
          {PLANS.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.1}>
              <div
                data-testid={`pricing-card-${p.id}`}
                className={`relative flex h-full flex-col rounded-2xl border p-8 transition-transform duration-300 hover:-translate-y-1 ${
                  p.popular ? "border-[#FF3333] bg-[#FF3333]/5" : "border-white/10 bg-[#050505]"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3.5 left-8 rounded-full bg-[#FF3333] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-white">
                    {t("pricing.popular")}
                  </span>
                )}
                <h3 className="font-syne text-2xl font-bold tracking-tight text-[#F2F2F2]">
                  {t(`pricing.${p.id}.name`)}
                </h3>
                <p className="mt-4 font-syne text-5xl font-extrabold tracking-tighter text-[#F2F2F2]">
                  {p.price}
                </p>
                <p className="mt-1 text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">{t("pricing.oneoff")}</p>
                <ul className="mt-8 flex flex-1 flex-col gap-4">
                  {Array.from({ length: p.features }, (_, f) => (
                    <li key={f} className="flex items-start gap-3 text-sm leading-relaxed text-[#A3A3A3]">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FF3333]/15">
                        <Check className="h-3 w-3 text-[#FF3333]" />
                      </span>
                      {t(`pricing.${p.id}.f${f + 1}`)}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`pricing-cta-${p.id}`}
                  onClick={() => onNavigate("kontakt")}
                  className={`group mt-10 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.03] ${
                    p.popular ? "bg-[#FF3333] text-white" : "border border-white/20 text-[#F2F2F2] hover:border-[#FF3333]"
                  }`}
                >
                  {t("pricing.cta")} {t(`pricing.${p.id}.name`)}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.3}>
          <p className="mt-10 text-sm text-[#A3A3A3]">{t("pricing.note")}</p>
        </FadeUp>
      </div>
    </section>
  );
};
